/**
 * Calculate date presets
 * @param preset Preset type
 * @returns Object with start and end dates in YYYY-MM-DD format
 */
export function getDatePreset(preset: "last-7" | "last-30" | "next-30" | "current-month" | "financial-year"): {
  start: string
  end: string
} {
  const today = new Date()
  const end = formatDateToYYYYMMDD(today)

  switch (preset) {
    case "last-7": {
      const start = new Date(today)
      start.setDate(start.getDate() - 6) // Today + 6 days back = 7 days
      return { start: formatDateToYYYYMMDD(start), end }
    }
    case "last-30": {
      const start = new Date(today)
      start.setDate(start.getDate() - 29) // Today + 29 days back = 30 days
      return { start: formatDateToYYYYMMDD(start), end }
    }
    case "next-30": {
      const start = new Date(today)
      start.setDate(start.getDate() + 1) // Start from tomorrow
      const end = new Date(today)
      end.setDate(end.getDate() + 30) // Today + 30 days forward
      return { start: formatDateToYYYYMMDD(start), end: formatDateToYYYYMMDD(end) }
    }
    case "current-month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      // Last day of current month
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      return { start: formatDateToYYYYMMDD(start), end: formatDateToYYYYMMDD(endOfMonth) }
    }
    case "financial-year": {
      // Financial year: Jan 1 to Dec 31 of current year
      const start = new Date(today.getFullYear(), 0, 1) // January 1st
      const endOfYear = new Date(today.getFullYear(), 11, 31) // December 31st
      return {
        start: formatDateToYYYYMMDD(start),
        end: formatDateToYYYYMMDD(endOfYear),
      }
    }
  }
}

/**
 * Format date to YYYY-MM-DD
 * @param date Date object
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

/**
 * Get default date range (financial year)
 * @returns Object with start and end dates
 */
export function getDefaultDateRange(): { start: string; end: string } {
  return getDatePreset("financial-year")
}

/**
 * Format date for chart X-axis display
 * @param dateString Date in YYYY-MM-DD format
 * @returns Formatted date string (e.g., "05 Oct")
 */
export function formatChartDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  })
}

/**
 * Format datetime for table display
 * @param dateTimeString ISO datetime string
 * @returns Formatted datetime string (e.g., "05 Oct 2025, 14:30")
 */
export function formatDateTime(dateTimeString: string | null | undefined): string {
  if (!dateTimeString) return "—"

  const date = new Date(dateTimeString)
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
}

